import type { ExportUpdatedPayload } from '@gozoki/api-types'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect } from 'react'
import { useTrackedExports } from '~/hooks/use-tracked-exports'
import { exportPollDelay, isDefinitivePollRefusal, isExportInFlight } from '~/lib/export-jobs'
import { exportTracker, inFlightExports, type TrackedExport } from '~/lib/export-tracker'
import { api } from '~/lib/tuyau'
import { getTuyauErrorStatus } from '~/lib/utils'
import { useExportUpdates } from '~/utils/websockets/useExportUpdates'
import { announceLostExport, announceSettledExport } from './export-delivery'

/**
 * Suivi des exports lancés depuis l'onglet (lot S8.4, conception S8 §2.9), monté à la racine
 * (`__root.tsx`) : il survit au changement d'écran, layout authentifié ou `_nosidebar`. Chaque export en cours est sondé
 * (`GET /exports/:id`, 2 s pendant 30 s puis 5 s, arrêté au statut final) ; `export:updated`
 * avance le sondage. Au statut final : téléchargement et toast, une seule fois.
 */
export function ExportJobsWatcher() {
  const queryClient = useQueryClient()
  const tracked = useTrackedExports()

  const onUpdate = useCallback(
    ({ id }: ExportUpdatedPayload) => {
      const entry = exportTracker.get(id)
      if (!entry || entry.settledStatus !== null) return
      void queryClient.invalidateQueries({
        queryKey: api.exports.show.queryKey({ params: { publicId: id } }),
      })
    },
    [queryClient],
  )
  useExportUpdates(onUpdate)

  return inFlightExports(tracked).map((entry) => (
    <TrackedExportFollower key={entry.id} entry={entry} />
  ))
}

function TrackedExportFollower({ entry }: { entry: TrackedExport }) {
  const { data, error } = useQuery(
    api.exports.show.queryOptions(
      { params: { publicId: entry.id } },
      {
        // Amorcé par la réponse `202` (`useExportJob`) : seuls le rythme et l'évènement relancent.
        staleTime: Number.POSITIVE_INFINITY,
        // Le sondage suivant est le nouvel essai.
        retry: false,
        refetchInterval: (query) =>
          exportPollDelay(query.state.data?.status ?? 'pending', Date.now() - entry.trackedAt),
        // Onglet en arrière-plan : le fichier part quand même à la fin.
        refetchIntervalInBackground: true,
      },
    ),
  )

  useEffect(() => {
    if (!data || isExportInFlight(data.status)) return
    const settled = exportTracker.settle(entry.id, data.status)
    if (settled) void announceSettledExport(data, settled.label)
  }, [data, entry.id])

  useEffect(() => {
    if (!error || !isDefinitivePollRefusal(getTuyauErrorStatus(error))) return
    const settled = exportTracker.settle(entry.id, 'failed')
    if (settled) announceLostExport(entry.id, settled.label, error)
  }, [error, entry.id])

  return null
}
